import {
    Navigate,
    useLocation,
    useNavigate,
    useRoutes,
} from "react-router-dom";
import { AnswerersPage } from "../pages/AnserwersPage";
import { ProfilePage } from "../pages/ProfilePage";
import { SigninPage } from "../pages/SigninPage";
import { Outlet, RouteObject } from "react-router-dom";
import { memo, useEffect, useState } from "react";
import { SignupPage } from "../pages/SignupPage";
import { HomePage } from "../pages/HomePage";
import { useAppState, useSetAppState } from "../stores/appContext";
import { useAuth } from "../features/auth/hooks/useAuth";
import { MemoryDetail } from "../pages/MemoryDetail";
import { Layout } from "../components/Layout";
import { SignupResultPage } from "../pages/SignupResultPage";
import { Path } from "./PagePath";
import { AnswererDetailPage } from "../pages/AnswererDetailPage";
import { AnswererDetailQmailSendFrequencyPage } from "../pages/AnswererDetailQmailSendFrequencyPage";
import { ErrorPage } from "../pages/ErrorPage";
import { useGTM } from "../utils/hooks/useGTM";
import { SendConfirmationCodePage } from "../pages/SendConfirmationCodePage";
import { SendConfirmationCodeCompletedPage } from "../pages/SendConfirmationCodeCompletedPage";
import { NewPasswordPage } from "../pages/NewPasswordPage";
import { NewPasswordCompletedPage } from "../pages/NewPasswordCompletedPage";
import { TermsPage } from "../pages/TermsPage";
import { UseStartMailRemindPage } from "../pages/UseStartMailRemindPage";
import featureFlags from "../../featureFlags.json";
import { EmailConfirmPage } from "../pages/EmailConfirmPage";
import {
    QueryClient,
    QueryClientProvider,
    useQueryClient,
} from "@tanstack/react-query";
import GeneratedQCandidateListPage from "../pages/GeneratedQCandidateListPage";
import { ActivitiesPage } from "../pages/ActivitiesPage";
import { WellnessChecksAnswerersPage } from "../pages/WellnessChecksAnswerersPage";
import { WellnessChecksPage } from "../pages/WellnessChecksPage";
import { WellnessCheckDetailPage } from "../pages/WellnessCheckDetailPage";
import { ActivityDetailPage } from "../pages/ActivityDetailPage";
import { ActivityReplyDetailPage } from "../pages/ActivityReplyDetailPage";
import { ReceiverNicknamePage } from "../pages/ReceiverNicknamePage";
import { AnswererRequiredInfoPage } from "../pages/AnswererRequiredInfoPage";
import { AnswererLineLinkPage } from "../pages/AnswererLineLinkPage";
import { QuestionAnswerersPage } from "../pages/QuestionAnswerersPage";
import { QuestionsPage } from "../pages/QuestionsPage";
import { CreateQuestionPage } from "../pages/CreateQuestionPage";
import { SenderEmailContentsConfirmPage } from "../pages/SenderEmailContentsConfirmPage";
import { SendEmailCompletedPage } from "../pages/SendEmailCompletedPage";
import { UpdateQuestionPage } from "../pages/UpdateQuestionPage";
import { SentQuestionDetailPage } from "../pages/SentQuestionDetailPage";
import { PostStoryPage } from "../pages/PostStoryPage";
import { StoryInfoPage } from "../pages/StoryInfoPage";
import { FavoriteMemoriesPage } from "../pages/FavoriteMemoriesPage";
import { PushNotificationTestPage } from "../pages/PushNotificationTestPage";
import { usePushTestPageVisible } from "../features/featureFlag/hooks/useFeatureEnabled";

const PushNotificationTestRoute: React.FC = () => {
    const visible = usePushTestPageVisible();
    if (!visible) {
        return <Navigate to={Path.notFound} replace />;
    }
    return <PushNotificationTestPage />;
};

const LayoutWrappedOutlet = () => {
    return (
        <Layout>
            <Outlet />
        </Layout>
    );
};

const ProtectedRoute: React.FC = () => {
    const [rendered, setRendered] = useState(false);
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const state = useAppState();
    const { isLoggedIn } = state;
    const setAppState = useSetAppState();
    const { fetchCSRFToken, verifyAndFetchToken } = useAuth();
    const queryClient = useQueryClient();
    useGTM();

    useEffect(() => {
        if (!rendered) {
            setRendered(true);
            return;
        }

        const checkAuth = async () => {
            await verifyAndFetchToken({
                fallback: () => {
                    queryClient.clear();
                    navigate({
                        pathname: Path.signin,
                        search: `?redirectTo=${encodeURIComponent(
                            pathname + search
                        )}`,
                    });
                },
            });
        };

        if (!isLoggedIn) checkAuth();
    }, [
        rendered,
        fetchCSRFToken,
        isLoggedIn,
        navigate,
        setAppState,
        pathname,
        search,
        queryClient,
        verifyAndFetchToken,
    ]);

    return isLoggedIn ? <LayoutWrappedOutlet /> : <></>;
};

const UnProtectedRoute: React.FC = () => {
    return <LayoutWrappedOutlet />;
};

export const ROUTES: RouteObject[] = [
    {
        path: Path.root,
        element: <ProtectedRoute />,
        children: [
            {
                index: true,
                element: <HomePage />,
            },
            {
                path: Path.answerers,
                element: <AnswerersPage />,
            },
            {
                path: Path.profile,
                element: <ProfilePage />,
            },
            {
                path: Path.activities,
                element: <ActivitiesPage />,
            },
            {
                path: Path.answerer,
                children: [
                    {
                        path: Path.answererDetail,
                        element: <AnswererDetailPage />,
                    },
                    {
                        path: Path.answererDetailQmailSendFrequency,
                        element: <AnswererDetailQmailSendFrequencyPage />,
                    },
                    {
                        path: Path.receivernickname,
                        element: <ReceiverNicknamePage />,
                    },
                    {
                        path: Path.answererRequiredInfo,
                        element: <AnswererRequiredInfoPage />,
                    },
                    {
                        path: Path.answererLineLink,
                        element: <AnswererLineLinkPage />,
                    },
                    {
                        path: Path.senderEmailConfirm,
                        element: <SenderEmailContentsConfirmPage />,
                    },
                    {
                        path: Path.sendEmailCompleted,
                        element: <SendEmailCompletedPage />,
                    },
                ],
            },
            {
                path: Path.memorydetail,
                element: <MemoryDetail />,
            },
            {
                path: Path.favoriteMemories,
                element: <FavoriteMemoriesPage />,
            },
            {
                path: Path.useStartMailRemind,
                element: <UseStartMailRemindPage />,
            },
            {
                path: Path.emailConfirm,
                element: <EmailConfirmPage />,
            },
            {
                path: Path.wellnessChecksAnswerers,
                element: <WellnessChecksAnswerersPage />,
            },
            {
                path: Path.wellnessChecks,
                element: <WellnessChecksPage />,
            },
            {
                path: Path.wellnessCheckDetail,
                element: <WellnessCheckDetailPage />,
            },
            {
                path: Path.generatedQCandidate,
                element: <GeneratedQCandidateListPage />,
            },
            {
                path: `${Path.activityDetail}/:id`,
                element: <ActivityDetailPage />,
            },
            {
                path: Path.activityReplyDetail,
                element: <ActivityReplyDetailPage />,
            },
            {
                path: Path.addQuestionAnswerers,
                element: <QuestionAnswerersPage />,
            },
            {
                path: `${Path.questionList}/:answererId`,
                element: <QuestionsPage />,
            },
            {
                path: `${Path.addQuestion}/:answererId`,
                element: <CreateQuestionPage />,
            },
            {
                path: `${Path.updateQuestion}/:answererId/:id`,
                element: <UpdateQuestionPage />,
            },
            {
                path: `${Path.sentQuestionDetail}/:answererId/:id`,
                element: <SentQuestionDetailPage />,
            },
            {
                path: Path.postStory,
                element: <PostStoryPage />,
            },
            {
                path: Path.storyInfo,
                element: <StoryInfoPage />,
            },
            {
                path: Path.pushTest,
                element: <PushNotificationTestRoute />,
            },
        ],
    },
    {
        path: Path.root,
        element: <UnProtectedRoute />,
        children: [
            {
                path: Path.signin,
                element: <SigninPage />,
            },
            ...(featureFlags.signup
                ? [
                      {
                          path: Path.signup,
                          element: <SignupPage />,
                      },
                      {
                          path: Path.signupResult,
                          element: <SignupResultPage />,
                      },
                  ]
                : []),
            {
                path: Path.terms,
                element: <TermsPage />,
            },
            {
                path: Path.error,
                element: <ErrorPage />,
            },
            {
                path: Path.passwordResetSendConfirmationCode,
                element: <SendConfirmationCodePage />,
            },
            {
                path: Path.passwordResetSendConfirmationCodeCompleted,
                element: <SendConfirmationCodeCompletedPage />,
            },
            {
                path: Path.passwordResetNewPassword,
                element: <NewPasswordPage />,
            },
            {
                path: Path.passwordResetNewPasswordCompleted,
                element: <NewPasswordCompletedPage />,
            },
            {
                path: "*", // 404
                element: <ErrorPage errorMessage="ページが見つかりません。" />,
            },
        ],
    },
];

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            // 6時間で鮮度切れ → フォーカス/再接続で再検証される
            staleTime: 6 * 60 * 60 * 1000,
            // 6時間でキャッシュ切れ → フォーカス/再接続で再検証される
            cacheTime: 6 * 60 * 60 * 1000,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
        },
    },
});

// eslint-disable-next-line react/display-name
export const PageRouter = memo(() => {
    const element = useRoutes(ROUTES);
    return (
        <>
            <QueryClientProvider client={queryClient}>
                {element}
            </QueryClientProvider>
        </>
    );
});
